<template>
  <b-row>
    <TextField
      v-model="user.name"
      name="name"
      :label="__('common.name')"
      class="col-md-5"
      required
    />

    <TextField
      v-model="user.email"
      name="email"
      :label="__('common.email')"
      class="col-md-4"
    />

    <SelectField
      v-model="user.defaultEntityId"
      name="default_entity_id"
      :label="__('common.entity')"
      class="col-md-3"
      required
      :readonly="isReadOnly"
      :options="entities"
      @onChange="onEntityChange"
    />

    <SelectField
      v-model="user.roles"
      name="roles[]"
      :label="__('roles.roles')"
      class="col-md-4"
      :options="roles"
      multiple
    />

    <TextField
      v-model="user.position"
      :label="__('users.position')"
      class="col-md-3"
      name="position"
    />

    <TextField
      v-model="user.phone"
      name="phone"
      :label="__('common.phone')"
      class="col-md-4"
    />

    <SelectField
      v-model="user.specialities"
      name="specialities[]"
      :label="__('users.specialities')"
      class="col-md-4"
      :options="UserModel.specialities()"
      multiple
    />

    <TextField
      v-model="user.mobile"
      name="mobile"
      :label="__('common.mobile')"
      class="col-md-4"
    />

    <SelectField
      v-model="user.notificationChannels"
      name="notification_channels[]"
      :label="__('notifications.popover-title')"
      class="col-md-3"
      :options="UserModel.notificationChannels()"
      multiple
    />

    <TextField
      v-model="user.addressHouse"
      name="address_house"
      :label="__('address.house')"
      class="col-md-2"
    />

    <TextField
      v-model="user.addressUnit"
      name="address_unit"
      :label="__('address.unit')"
      class="col-md-2"
    />

    <TextField
      v-model="user.addressStreet"
      name="address_street"
      :label="__('address.street')"
      class="col-md-4"
    />

    <TextField
      v-model="user.addressTown"
      name="address_town"
      :label="__('address.town')"
      class="col-md-4"
    />

    <TextField
      v-model="user.addressPostCode"
      name="address_post_code"
      :label="__('address.post-code')"
      class="col-md-3"
    />

    <SelectField
      v-model="user.addressCountry"
      name="address_country"
      :label="__('address.country')"
      class="col-md-4"
      required
      autocomplete="off"
      :options="translatedCountries"
      @onChange="fetchCounties"
    />

    <SelectField
      v-if="user.addressCountry && counties.length"
      v-model="user.addressCounty"
      :options="counties"
      name="address_county"
      :label="__('address.county')"
      class="col-md-3"
    />

    <TextField
      v-else
      v-model="user.addressCounty"
      name="address_county"
      :label="__('address.county')"
      class="col-md-3"
    />

    <SelectField
      v-model="user.language"
      :options="languages"
      name="language"
      required
      :label="__('common.language')"
      class="col-md-3"
    />

    <b-col v-if="!user.id" cols="12">
      <hr />
    </b-col>

    <EditorField
      v-model="user.emailSignature"
      name="email_signature"
      :label="__('email.signature')"
      class="col-12"
      :editor-options="editorOptions"
      height="250px"
    />

    <TextAreaField
      v-model="user.notes"
      name="notes"
      :label="__('common.notes')"
      :description="__('users.notes')"
      :rows="4"
      class="col-12"
    />
    <FormActions :on-submit="save" :on-cancel="cancel" />
  </b-row>
</template>

<script>
import {
  COUNTRIES_GETTER,
  DEFAULT_COUNTRY_GETTER,
  UI_DEFAULT_LOCALE,
} from '@/Modules/Base/SettingModule.js'
import { EntityModelType } from '@/Modules/Entity/EntityModel.js'
import QuotePrefixedRouteMixin from '@/Modules/Quote/Mixins/QuotePrefixedRouteMixin.js'
import TranslationLibraryService from '@/Modules/TranslationLibrary/TranslationLibraryService.js'
import UserModel from '@/Modules/User/UserModel.js'
import UserService from '@/Modules/User/UserService.js'
import DatasetService from '@/Modules/Dataset/DatasetService.js'
import { mapGetters } from 'vuex'
import { getConfig } from '@/General/Form/Fields/Editor/config.js'
import TranslatedCountries from '@/General/Mixins/TranslatedCountries.js'

export default {
  name: 'EditForm',

  mixins: [QuotePrefixedRouteMixin, TranslatedCountries],

  props: {
    userData: {
      type: Object,
      default: () => {
        return {}
      },
    },
    roles: {
      type: Array,
      default: () => {
        return []
      },
    },
    entities: {
      type: Array,
      default: () => {
        return []
      },
    },
    onSuccess: {
      type: Function,
      default: null,
    },
    onCancel: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      user: new UserModel(this.userData),
      UserModel: UserModel,
      counties: [],
      languages: [],
    }
  },

  computed: {
    ...mapGetters('SettingModule', {
      countries: COUNTRIES_GETTER,
      defaultCountry: DEFAULT_COUNTRY_GETTER,
      uiDefaultLocale: UI_DEFAULT_LOCALE,
    }),

    isReadOnly: function () {
      return (
        !this.authUser().entity.children.length &&
        this.authUser().entity.type === EntityModelType.AGENT
      )
    },
  },

  watch: {
    entities(newValue, oldValue) {
      if (_.isArray(newValue) && !_.isEqual(newValue, oldValue)) {
        this.onEntityChange()
      }
    },
  },

  async created() {
    this.onEntityChange()
    this.fetchCounties(true)
    this.editorOptions = {
      modules: {
        toolbar: getConfig({ image: true }),
      },
      placeholder: this.__('email.add-signature'),
    }
    const assignedLanguages = await TranslationLibraryService.assigned()
    this.languages = _.map(
      assignedLanguages.filter((item) => !!item),
      (language) => {
        return {
          value: this.__(language.languageCode),
          label: this.__(language.name),
        }
      }
    )

    if (!this.user.language && this.uiDefaultLocale) {
      this.user.language = this.uiDefaultLocale
    }
  },

  methods: {
    onEntityChange(event) {
      if (this.user.id) {
        return
      }
      const entity = this.entities.find(
        (entity) =>
          entity.value === this.$selectValue(this.user, 'defaultEntityId')
      )

      if (entity && entity.meta) {
        this.assignUserMeta(entity.toCamelCase())
      }
    },

    fetchCounties(initial) {
      if (!this.user.addressCountry && initial) {
        this.user.addressCountry = this.defaultCountry
      }

      if (initial !== true) {
        this.user.addressCounty = null
      }

      if (!this.user.addressCountry) {
        this.counties = []
        return
      }

      let fetchValue = this.user.addressCountry

      if (typeof this.user.addressCounty === 'object') {
        fetchValue = this.user.addressCountry.value
      }

      DatasetService.listOptionsByTagAndValue(
        'counties',
        'country_name',
        fetchValue
      ).then((counties) => (this.counties = counties))
    },

    assignUserMeta(meta) {
      this.user.addressCountry = meta?.addressCountry
      this.user.addressCounty = meta?.addressCounty
      this.user.addressHouse = meta?.addressHouse
      this.user.addressPostCode = meta?.addressPostCode
      this.user.addressStreet = meta?.addressStreet
      this.user.addressTown = meta?.addressTown
      this.user.addressUnit = meta?.addressUnit
    },

    save() {
      if (this.user.id) {
        UserService.update(this.user.id, this.getUserPayload())
          .then((user) => {
            this.$emit('updated', user)
            this.onSuccess && this.onSuccess(user)
          })
          .catch((error) => this.setErrorsFromResponse(error))

        return
      }

      UserService.create(this.getUserPayload())
        .then((user) => {
          this.$emit('created', user)
          this.onSuccess && this.onSuccess(user)
        })
        .catch((error) => this.setErrorsFromResponse(error))
    },
    cancel() {
      this.onCancel && this.onCancel()
    },

    getUserPayload() {
      return {
        email: this.user.email,
        name: this.user.name,
        roles: this.user.roles,
        defaultEntityId: this.$selectValue(this.user, 'defaultEntityId'),
        phone: this.user.phone,
        mobile: this.user.mobile,
        language: this.$selectValue(this.user, 'language'),
        addressHouse: this.user.addressHouse,
        addressStreet: this.user.addressStreet,
        addressTown: this.user.addressTown,
        addressPostCode: this.user.addressPostCode,
        addressCounty: this.$selectValue(this.user, 'addressCounty'),
        addressUnit: this.user.addressUnit,
        addressCountry: this.$selectValue(this.user, 'addressCountry'),
        notificationChannels: this.user.notificationChannels,
        specialities: this.formatSpecialities(this.user.specialities),
        notes: this.user.notes,
        emailSignature: this.user.emailSignature,
        position: this.user.position,
      }
    },

    formatSpecialities(specialities) {
      if (_.isEmpty(specialities)) {
        return []
      }

      return specialities.filter((speciality) => {
        if (!_.isNull(speciality) && speciality.value) {
          return speciality.value
        }
      })
    },
  },
}
</script>
